import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Loader2, Star, Image as ImageIcon, Link as LinkIcon, GripVertical, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable image uploader.
 * Props:
 *  - images: array of {url, alt?, order?, storage_path?}
 *  - onChange: (newImages) => void
 *  - token: admin bearer token (for upload/delete)
 *  - maxImages: default 10
 */
export default function ImageUploader({ images = [], onChange, token, maxImages = 10 }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [dragIndex, setDragIndex] = useState(null);
  const [replacingIdx, setReplacingIdx] = useState(null);
  const pendingReplaceIdx = useRef(null);
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);

  const uploadSingle = async (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error(`"${file.name}" no es una imagen`);
      return null;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`"${file.name}" supera 5MB`);
      return null;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'products');
    const res = await fetch(`${API}/admin/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Error' }));
      throw new Error(err.detail || 'Upload failed');
    }
    return await res.json();
  };

  const handleFiles = useCallback(async (files) => {
    const list = Array.from(files);
    if (images.length + list.length > maxImages) {
      toast.error(`Máximo ${maxImages} fotos por producto`);
      return;
    }
    setUploading(true);
    setProgress({ current: 0, total: list.length });
    const uploaded = [];
    for (let i = 0; i < list.length; i++) {
      try {
        const result = await uploadSingle(list[i]);
        if (result) {
          uploaded.push({
            url: result.url,
            alt: list[i].name.replace(/\.[^.]+$/, ''),
            storage_path: result.storage_path,
            order: images.length + uploaded.length,
          });
        }
      } catch (e) {
        toast.error(`Error con ${list[i].name}: ${e.message}`);
      }
      setProgress({ current: i + 1, total: list.length });
    }
    if (uploaded.length > 0) {
      onChange([...images, ...uploaded]);
      toast.success(`${uploaded.length} foto(s) subidas`);
    }
    setUploading(false);
    setProgress({ current: 0, total: 0 });
  }, [images, maxImages, onChange, token]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  // Replace a specific image in place (keeps the index, soft-deletes the old storage file)
  const replaceAt = async (idx, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(`"${file.name}" no es una imagen`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`"${file.name}" supera 5MB`);
      return;
    }
    const oldImg = images[idx];
    setReplacingIdx(idx);
    try {
      const result = await uploadSingle(file);
      if (!result) return;
      const next = [...images];
      next[idx] = {
        url: result.url,
        alt: file.name.replace(/\.[^.]+$/, ''),
        storage_path: result.storage_path,
        order: idx,
      };
      onChange(next);
      toast.success(idx === 0 ? 'Foto principal reemplazada' : 'Foto reemplazada');
      // Soft-delete old storage (best effort)
      if (oldImg?.storage_path) {
        fetch(`${API}/admin/upload?storage_path=${encodeURIComponent(oldImg.storage_path)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    } catch (e) {
      toast.error(`Error al reemplazar: ${e.message}`);
    } finally {
      setReplacingIdx(null);
    }
  };

  const triggerReplace = (idx) => {
    pendingReplaceIdx.current = idx;
    replaceInputRef.current?.click();
  };

  const addFromUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      toast.error('La URL debe empezar con http:// o https://');
      return;
    }
    if (images.length >= maxImages) {
      toast.error(`Máximo ${maxImages} fotos`);
      return;
    }
    onChange([...images, { url: u, alt: '', order: images.length }]);
    setUrlInput('');
    setShowUrlInput(false);
    toast.success('Imagen agregada desde URL');
  };

  const removeAt = async (idx) => {
    const img = images[idx];
    const next = images.filter((_, i) => i !== idx).map((x, i) => ({ ...x, order: i }));
    onChange(next);
    // Soft-delete in storage if it was uploaded (not external URL)
    if (img?.storage_path) {
      try {
        await fetch(`${API}/admin/upload?storage_path=${encodeURIComponent(img.storage_path)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) { /* silent */ }
    }
  };

  const makeMain = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    const [item] = next.splice(idx, 1);
    next.unshift(item);
    onChange(next.map((x, i) => ({ ...x, order: i })));
  };

  // Drag-reorder
  const onCardDragStart = (i) => setDragIndex(i);
  const onCardDragOver = (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    const next = [...images];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    setDragIndex(i);
    onChange(next.map((x, n) => ({ ...x, order: n })));
  };
  const onCardDragEnd = () => setDragIndex(null);

  const resolveSrc = (url) => {
    // If relative /api/media/..., prefix with REACT_APP_BACKEND_URL
    if (url?.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  return (
    <div data-testid="image-uploader">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold text-slate-600 uppercase">
          Fotos del producto ({images.length}/{maxImages})
        </label>
        {images.length > 0 && (
          <span className="text-[10px] text-slate-400">⭐ La primera es la foto principal · arrastra para reordenar</span>
        )}
      </div>

      {/* Grid de imágenes */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
          {images.map((img, i) => (
            <div
              key={`${img.url}-${i}`}
              draggable
              onDragStart={() => onCardDragStart(i)}
              onDragOver={(e) => onCardDragOver(e, i)}
              onDragEnd={onCardDragEnd}
              className={`relative group aspect-square rounded-xl overflow-hidden bg-slate-100 border-2 ${i === 0 ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9]/20' : 'border-slate-200'} ${dragIndex === i ? 'opacity-50' : ''}`}
              data-testid={`image-card-${i}`}
            >
              <img src={resolveSrc(img.url)} alt={img.alt || 'Producto'} className="w-full h-full object-cover" />
              {i === 0 && (
                <div className="absolute top-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#0EA5E9] text-white text-[9px] font-bold">
                  <Star className="w-2.5 h-2.5" fill="white" strokeWidth={0} /> Principal
                </div>
              )}
              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => triggerReplace(i)} title="Reemplazar" className="w-6 h-6 rounded-md bg-white/90 hover:bg-[#0EA5E9] hover:text-white text-[#0A2540] flex items-center justify-center shadow" data-testid={`image-replace-${i}`}>
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                {i !== 0 && (
                  <button type="button" onClick={() => makeMain(i)} title="Hacer principal" className="w-6 h-6 rounded-md bg-white/90 hover:bg-white text-[#0A2540] flex items-center justify-center shadow" data-testid={`image-make-main-${i}`}>
                    <Star className="w-3.5 h-3.5" />
                  </button>
                )}
                <button type="button" onClick={() => removeAt(i)} title="Eliminar" className="w-6 h-6 rounded-md bg-white/90 hover:bg-red-500 hover:text-white text-red-500 flex items-center justify-center shadow" data-testid={`image-delete-${i}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {replacingIdx === i && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center" data-testid={`image-replacing-${i}`}>
                  <Loader2 className="w-6 h-6 animate-spin text-white" />
                </div>
              )}
              <div className="absolute bottom-1 left-1 w-5 h-5 rounded bg-black/60 text-white text-[10px] font-bold flex items-center justify-center">
                <GripVertical className="w-3 h-3 text-white/80" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      {images.length < maxImages && (
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`relative rounded-xl border-2 border-dashed p-4 text-center transition-colors ${dragOver ? 'border-[#0EA5E9] bg-sky-50' : 'border-slate-300 bg-slate-50'} ${uploading ? 'opacity-70 pointer-events-none' : ''}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 py-3">
              <Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" />
              <div className="text-xs text-slate-600">Subiendo {progress.current} de {progress.total}…</div>
              <div className="w-full max-w-xs h-1.5 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full bg-[#0EA5E9] transition-all" style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }} />
              </div>
            </div>
          ) : (
            <>
              <ImageIcon className="w-8 h-8 mx-auto text-slate-400 mb-2" strokeWidth={1.5} />
              <div className="text-sm text-slate-600 mb-2">
                <strong>Arrastra tus fotos aquí</strong> o haz clic en un botón abajo.
              </div>
              <div className="text-[10px] text-slate-400 mb-3">JPG, PNG, WebP o GIF · máx. 5MB c/u · máx. {maxImages} fotos</div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0EA5E9] text-white text-xs font-semibold hover:bg-sky-600 shadow"
                  data-testid="image-uploader-browse"
                >
                  <Upload className="w-4 h-4" />Examinar archivos
                </button>
                <button
                  type="button"
                  onClick={() => setShowUrlInput(!showUrlInput)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
                  data-testid="image-uploader-url-toggle"
                >
                  <LinkIcon className="w-4 h-4" />Desde URL
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => { if (e.target.files?.length) { handleFiles(e.target.files); e.target.value = ''; } }}
                data-testid="image-uploader-input"
              />
              {showUrlInput && (
                <div className="flex gap-2 mt-3">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://ejemplo.com/foto.jpg"
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="image-uploader-url-input"
                  />
                  <button
                    type="button"
                    onClick={addFromUrl}
                    className="px-4 py-2 rounded-lg bg-[#0A2540] text-white text-xs font-semibold hover:bg-[#050E1F]"
                    data-testid="image-uploader-url-add"
                  >
                    Agregar
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {images.length >= maxImages && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
          <AlertCircle className="w-4 h-4" />Alcanzaste el máximo de {maxImages} fotos. Elimina alguna para agregar más.
        </div>
      )}

      {/* Hidden input used exclusively by the per-card "Replace" button */}
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const idx = pendingReplaceIdx.current;
          const file = e.target.files?.[0];
          e.target.value = '';
          pendingReplaceIdx.current = null;
          if (idx !== null && idx !== undefined && file) replaceAt(idx, file);
        }}
        data-testid="image-uploader-replace-input"
      />
    </div>
  );
}
